import { useEffect, useRef, useState } from 'react';
import { Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useContent } from '../context/ContentContext';

/**
 * Inline-editable text block for Super Admins.
 *
 * Usage:
 *   <EditableText contentKey="home.hero.title" multiline={false} as="h1" className="...">
 *     Fix Together, Waste Less
 *   </EditableText>
 *
 * - Non-editors see the text exactly as-is (from DB fallback or children).
 * - Super Admins see a pencil on hover; clicking opens an inline input/textarea.
 */
export function EditableText({
  contentKey,
  children,
  multiline = false,
  as: Tag = 'span',
  className = '',
  inputClassName = '',
  placeholder = '',
}) {
  const { get, save, isEditor } = useContent();
  const fallback = typeof children === 'string' ? children : '';
  const stored = get(contentKey, fallback);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(stored);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setDraft(stored); }, [stored]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, [editing]);

  const commit = async () => {
    if (draft === stored) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await save(contentKey, draft);
      toast.success('Saved');
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(stored);
    setEditing(false);
  };

  // Read-only mode for non-editors
  if (!isEditor) {
    return <Tag className={className}>{stored}</Tag>;
  }

  // Editor mode (logged-in super_admin) — hover to reveal pencil
  if (!editing) {
    return (
      <Tag
        className={`group relative inline ${className}`}
        data-testid={`editable-${contentKey}`}
      >
        {stored}
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditing(true); }}
          title="Edit text"
          data-testid={`edit-${contentKey}`}
          className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center justify-center w-6 h-6 rounded bg-[#E87B6A] text-white align-middle shadow-md hover:bg-[#d66a59]"
        >
          <Pencil className="w-3 h-3" />
        </button>
      </Tag>
    );
  }

  // Editing — inline input/textarea with save/cancel
  const sharedProps = {
    ref: inputRef,
    value: draft,
    onChange: (e) => setDraft(e.target.value),
    onKeyDown: (e) => {
      if (e.key === 'Escape') cancel();
      else if (e.key === 'Enter' && !multiline) { e.preventDefault(); commit(); }
      else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && multiline) { e.preventDefault(); commit(); }
    },
    placeholder,
    disabled: saving,
    'data-testid': `editor-${contentKey}`,
    className: `block w-full text-inherit font-inherit bg-white text-gray-900 border-2 border-[#E87B6A] rounded px-2 py-1 outline-none focus:ring-2 focus:ring-[#E87B6A]/50 ${inputClassName}`,
  };

  return (
    <span className={`relative inline-block align-top w-full ${className}`}>
      {multiline ? <textarea rows={Math.max(2, Math.ceil((draft || '').length / 80))} {...sharedProps} /> : <input type="text" {...sharedProps} />}
      <span className="flex gap-2 mt-2">
        <button
          type="button"
          onClick={commit}
          disabled={saving}
          data-testid={`save-${contentKey}`}
          className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium bg-[#E87B6A] hover:bg-[#d66a59] text-white rounded shadow disabled:opacity-50"
        >
          <Check className="w-3 h-3" /> {saving ? 'Saving...' : 'Save'}
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={saving}
          data-testid={`cancel-${contentKey}`}
          className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium bg-gray-200 hover:bg-gray-300 text-gray-800 rounded"
        >
          <X className="w-3 h-3" /> Cancel
        </button>
        <span className="text-xs text-gray-500 self-center">
          {multiline ? 'Ctrl/⌘+Enter to save · Esc to cancel' : 'Enter to save · Esc to cancel'}
        </span>
      </span>
    </span>
  );
}
